import { BRAND, type ColorMode } from '@chukua/common';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DARK_QUERY, readPreference, resolveColorMode, writePreference, type ColorModePreference } from './color-mode';

interface ColorModeContextValue {
  preference: ColorModePreference;
  mode: ColorMode;
  setPreference: (preference: ColorModePreference) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ColorModePreference>(() => readPreference(safeStorage()));
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // `system` tracks the OS live — e.g. a laptop switching to dark at sunset.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode = resolveColorMode(preference, systemDark);

  // Keep <html> in step (the pre-paint script set it first) so the page
  // background, native form controls and scrollbars match.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset['theme'] = mode;
    root.style.colorScheme = mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BRAND.palettes[mode].surface);
  }, [mode]);

  const setPreference = useCallback((next: ColorModePreference) => {
    setPreferenceState(next);
    writePreference(safeStorage(), next);
  }, []);

  const value = useMemo(() => ({ preference, mode, setPreference }), [preference, mode, setPreference]);
  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used inside <ColorModeProvider>');
  return ctx;
}
